import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Button,
  CircularProgress,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { JobCategory_Entity } from "../../entities/jobCategory";
import {
  formOpenSelector,
  selectedJobCategoryID,
  setFormOpen,
  setSelectedJobCategoryID,
} from "../../redux/filmsReducer";
import {
  useCreateJobCategoryMutation,
  useDeleteJobCategoryMutation,
  useUpdateJobCategoryMutation,
} from "../../redux/jobCategory/jobCategoryEndpoints";
import {
  useJobCategories,
  useJobCategory,
} from "../../redux/jobCategory/jobCategoryHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";

/**
 *
 * @returns {ReactElement} FormJobCategory page
 */
export function FormJobCategory() {
  const jobCategoryID = useSelector(selectedJobCategoryID);
  const { jobCategory, isLoading } = useJobCategory(jobCategoryID);
  const open = useSelector(formOpenSelector("jobCategory"));
  const [_jobCategory, setJobCategory] = useState<Partial<JobCategory_Entity>>(
    {}
  );

  const [createJobCategory, { isLoading: isCreating }] =
    useCreateJobCategoryMutation();
  const [updateJobCategory, { isLoading: isUpdating }] =
    useUpdateJobCategoryMutation();
  const [deleteJobCategory] = useDeleteJobCategoryMutation();
  const { jobCategories } = useJobCategories();
  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (jobCategory?.id) {
      setJobCategory(jobCategory);
    }
  }, [jobCategory]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "jobCategory" }));
    dispatch(setSelectedJobCategoryID(undefined));
  };

  const save = () => {
    if (jobCategoryID && _jobCategory) {
      updateJobCategory({ id: jobCategoryID, body: _jobCategory });
    } else if (_jobCategory) {
      createJobCategory(_jobCategory)
        .unwrap()
        .then((e) => {
          dispatch(setSelectedJobCategoryID(e.id));
        });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Job Category. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (jobCategoryID) deleteJobCategory(jobCategoryID);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 420 }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa fa-briefcase" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {jobCategoryID ? "Edit" : "Create"} Job Category
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <Input
                startDecorator="Name:"
                value={_jobCategory.name ?? ""}
                onChange={(e) =>
                  setJobCategory((v) => ({ ...v, name: e.target.value }))
                }
                type="text"
                autoFocus
              />
            </Box>
          </Box>
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {jobCategoryID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {jobCategoryID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
